import React, { useEffect, useState } from "react";
import axios from "../../api/axiosInstance";
import toast from "react-hot-toast";
import dayjs from "dayjs";
import { Link } from "react-router-dom";
import GlobalLoader from "./GlobalLoader";
import { FiMoreVertical } from "react-icons/fi";
import EmptyStateCentered from "../static/EmptyStateCentered";

const InterestsProfilesListPage = () => {
  const [sentProfiles, setSentProfiles] = useState([]);
  const [receivedProfiles, setReceivedProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("sent");
  const [menuOpenId, setMenuOpenId] = useState(null);

  const calculateAge = (dob) => dayjs().diff(dayjs(dob), "year");

  const fetchProfiles = async () => {
    try {
      setLoading(true);
      const res = await axios.get("/api/interests-profiles/", {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
        },
      });
      setSentProfiles(res.data.sent);
      setReceivedProfiles(res.data.received);
      setLoading(false);
    } catch (error) {
      console.error(error.response?.data || error.message);
      toast.error("Failed to fetch interest profiles");
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProfiles();
    const handleClickOutside = () => setMenuOpenId(null);
    window.addEventListener("click", handleClickOutside);
    return () => window.removeEventListener("click", handleClickOutside);
  }, []);

  const handleDeleteInterest = async (interestId, profileData) => {
    // Optimistically remove from UI
    setSentProfiles((prev) => prev.filter((p) => p.interest_id !== interestId));
    setMenuOpenId(null);

    let undoTriggered = false;

    const undoToastId = toast.custom(
      (t) => (
        <div className="bg-white border rounded shadow p-3 flex items-center justify-between w-72">
          <span className="text-sm">Interest deleted</span>
          <button
            onClick={async () => {
              undoTriggered = true;
              toast.dismiss(t.id);
              try {
                await axios.post(
                  "/api/interests/",
                  { receiver: profileData.user.id, status: "sent" },
                  {
                    headers: {
                      Authorization: `Bearer ${localStorage.getItem(
                        "accessToken"
                      )}`,
                    },
                  }
                );
                setSentProfiles((prev) => [profileData, ...prev]);
                toast.success("Undo successful, interest restored.");
              } catch (error) {
                console.error(error.response?.data || error.message);
                toast.error("Failed to restore interest.");
              }
            }}
            className="ml-4 text-pink-600 text-sm hover:underline"
          >
            Undo
          </button>
        </div>
      ),
      { duration: 5000 }
    );

    try {
      await axios.delete(`/api/delete-interest/${interestId}/`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
        },
      });
      if (!undoTriggered) {
        toast.success("Interest permanently deleted.");
      }
    } catch (error) {
      console.error(error.response?.data || error.message);
      toast.error("Failed to delete interest.");
      // Revert on error
      setSentProfiles((prev) => [profileData, ...prev]);
    }
  };

  if (loading) {
    return <GlobalLoader />;
  }

  const displayedProfiles =
    activeTab === "sent" ? sentProfiles : receivedProfiles;

  return (
    <div className="p-4 w-full mx-auto overflow-y-auto">
      <div className="flex justify-center gap-4 mb-6">
        <button
          onClick={() => setActiveTab("sent")}
          className={`px-4 py-2 rounded ${
            activeTab === "sent" ? "bg-pink-600 text-white" : "bg-gray-200"
          }`}
        >
          Interests Sent ({sentProfiles.length})
        </button>
        <button
          onClick={() => setActiveTab("received")}
          className={`px-4 py-2 rounded ${
            activeTab === "received" ? "bg-pink-600 text-white" : "bg-gray-200"
          }`}
        >
          Interests Received ({receivedProfiles.length})
        </button>
      </div>

      {displayedProfiles.length === 0 ? (
        <p className="text-center text-gray-500 mt-10">
          <EmptyStateCentered/>
          {/* No profiles to display. */}
        </p>
      ) : (
        <div className="flex flex-col gap-4">
          {displayedProfiles.map((match) => (
            <div
              key={match.interest_id}
              className="bg-white rounded shadow p-4 flex flex-col sm:flex-row items-center sm:items-start gap-4 relative"
              onClick={(e) => e.stopPropagation()}
            >
              <Link to={`/profile/${match.user.id}`}>
                <img
                  src={
                    match.photo || require("../../assets/default_image.webp")
                  }
                  alt={match.user.first_name}
                  className="w-32 h-32 object-cover rounded text-decoration-none"
                />
              </Link>
              <div className="flex-1">
                <Link
                  to={`/profile/${match.user.id}`}
                  className="flex-shrink-0 text-decoration-none"
                >
                  <p className="font-semibold text-lg">
                    {match.user.first_name} {match.user.last_name}
                  </p>
                  <p className="text-sm text-gray-600 mb-2">
                    {calculateAge(match.dob)} yrs • {match.height_feet}'
                    {match.height_inches}" • {match.religion} •{" "}
                    {match.marital_status} • {match.highest_education} •{" "}
                    {match.employed_in} • {match.city}
                  </p>
                </Link>
                {activeTab === "sent" && (
                  <p className="text-xs text-gray-500 mt-2">
                    You sent an interest on{" "}
                    {dayjs(match.interest_created_at).format("D MMM YY")}

                  </p>
                )}
                {activeTab === "received" && (
                  <p className="text-xs text-gray-500 mt-2">
                    Sent you an interest on{" "}
                   {dayjs(match.interest_created_at).format("D MMM YY")}

                  </p>
                )}
              </div>

              {activeTab === "sent" && (
                <div className="absolute top-2 right-2">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setMenuOpenId(
                        menuOpenId === match.interest_id
                          ? null
                          : match.interest_id
                      );
                    }}
                  >
                    <FiMoreVertical size={20} />
                  </button>
                  {menuOpenId === match.interest_id && (
                    <div className="absolute right-0 mt-2 w-40 bg-white border rounded shadow z-50">
                      <button
                        onClick={() =>
                          handleDeleteInterest(match.interest_id, match)
                        }
                        className="block w-full text-left px-4 py-2 text-sm hover:bg-gray-100"
                      >
                        Delete Interest
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default InterestsProfilesListPage;
